import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lumis_user_token')}` });

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function PlanoViewerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isPreview = id === 'preview';
  const [plano, setPlano] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isPreview) {
      const raw = sessionStorage.getItem('lumis_plano_preview');
      if (!raw) {
        toast.error('Plano não encontrado. Gere um novo plano pelo chat.');
        navigate('/');
        return;
      }
      const { conteudo, module_name } = JSON.parse(raw);
      setPlano({ conteudo, module_name, created_at: new Date().toISOString() });
      setLoading(false);
    } else {
      axios.get(`${API}/planos/${id}`, { headers: authHeaders() })
        .then(res => setPlano(res.data))
        .catch(() => toast.error('Plano não encontrado.'))
        .finally(() => setLoading(false));
    }
  }, [id, isPreview, navigate]);

  if (loading) return (
    <div className="min-h-screen bg-[#EEF2FF] flex items-center justify-center">
      <div className="text-[#1E3A8A] text-3xl animate-pulse font-outfit">✦</div>
    </div>
  );

  if (!plano) return null;

  const { conteudo, module_name, created_at } = plano;

  return (
    <div className="min-h-screen bg-[#EEF2FF] print:bg-white" data-testid="plano-viewer-page">
      {/* Barra de navegação — não imprime */}
      <div className="print:hidden sticky top-0 z-10 bg-[#EEF2FF]/95 backdrop-blur-xl border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate(isPreview ? '/' : '/planos')}
          className="p-2 rounded-full hover:bg-gray-100 transition-all text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
        </button>
        <button
          data-testid="plano-print-btn"
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope text-sm transition-colors"
        >
          <Printer size={15} /> Imprimir
        </button>
      </div>

      {isPreview && (
        <div className="print:hidden mx-4 mt-4 bg-[#DBEAFE] border border-[#1E3A8A]/20 rounded-2xl p-4 text-center">
          <p className="text-[#1E3A8A] text-sm font-manrope mb-2">
            Crie uma conta gratuita para salvar este plano permanentemente e acessá-lo sempre.
          </p>
          <Link to="/register" className="inline-block px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope text-sm transition-colors">
            Criar conta grátis
          </Link>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-5 py-8 space-y-8 print:text-black">
        <div className="text-center space-y-1">
          <p className="text-[#1E3A8A] print:text-black font-outfit text-2xl">✦ Lumis</p>
          <p className="text-gray-500 print:text-black text-sm font-manrope">Plano de Ação · {module_name}</p>
          <p className="text-gray-400 print:text-black text-xs font-manrope">{fmtDate(created_at)}</p>
        </div>

        {conteudo.frase_abertura && (
          <p className="text-gray-900 print:text-black font-manrope italic text-center leading-relaxed">{conteudo.frase_abertura}</p>
        )}

        {conteudo.contexto_atual && (
          <section className="space-y-3">
            <h2 className="text-[#1E3A8A] print:text-black font-outfit font-semibold text-sm uppercase tracking-wide">Onde você está agora</h2>
            {conteudo.contexto_atual.split('\n\n').map((p, i) => (
              <p key={i} className="text-gray-700 print:text-black font-manrope text-sm leading-relaxed">{p}</p>
            ))}
          </section>
        )}

        {conteudo.insights?.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-[#1E3A8A] print:text-black font-outfit font-semibold text-sm uppercase tracking-wide">O que emergiu na sua conversa</h2>
            {conteudo.insights.map((ins, i) => (
              <div key={i} className="border-b border-gray-200 print:border-gray-300 pb-3">
                <p className="text-gray-900 print:text-black font-manrope font-semibold text-sm">{ins.titulo}</p>
                <p className="text-gray-500 print:text-black font-manrope text-sm mt-1">{ins.descricao}</p>
                {ins.o_que_a_pesquisa_diz && (
                  <p className="text-amber-700 print:text-black font-manrope text-xs italic mt-2">📖 {ins.o_que_a_pesquisa_diz}</p>
                )}
              </div>
            ))}
          </section>
        )}

        {conteudo.plano_de_acao?.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-[#1E3A8A] print:text-black font-outfit font-semibold text-sm uppercase tracking-wide">Plano de ação estratégico</h2>
            {conteudo.plano_de_acao.map((passo, i) => (
              <div key={i} className="bg-white print:bg-white print:border print:border-gray-300 rounded-2xl p-4 space-y-2">
                <p className="text-gray-900 print:text-black font-manrope font-bold text-sm">Passo {i + 1} — {passo.nome}</p>
                {passo.por_que_funciona && <p className="text-gray-700 print:text-black font-manrope text-sm">{passo.por_que_funciona}</p>}
                {passo.exemplo_real && <p className="text-emerald-700 print:text-black font-manrope text-xs italic">✦ {passo.exemplo_real}</p>}
                {passo.estrategia && <p className="text-gray-700 print:text-black font-manrope text-sm">{passo.estrategia}</p>}
                {passo.fala_literal && (
                  <p className="text-[#1E3A8A] print:text-black font-manrope text-sm italic">💬 "{passo.fala_literal}"</p>
                )}
                {passo.gesto && <p className="text-gray-500 print:text-black font-manrope text-xs">Gesto: {passo.gesto}</p>}
                {passo.se_reagir && <p className="text-gray-500 print:text-black font-manrope text-xs">Se reagir: {passo.se_reagir}</p>}
              </div>
            ))}
          </section>
        )}

        {conteudo.exercicios?.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-[#1E3A8A] print:text-black font-outfit font-semibold text-sm uppercase tracking-wide">Exercícios para praticar</h2>
            {conteudo.exercicios.map((ex, i) => (
              <div key={i} className="bg-white print:bg-white print:border print:border-gray-300 rounded-2xl p-4 space-y-1">
                <p className="text-emerald-700 print:text-black font-manrope font-bold text-sm">{i + 1}. {ex.nome}</p>
                {ex.base && <p className="text-gray-400 print:text-black font-manrope text-xs italic">{ex.base}</p>}
                {ex.instrucao && <p className="text-gray-700 print:text-black font-manrope text-sm">{ex.instrucao}</p>}
                {ex.frequencia && <p className="text-emerald-700 print:text-black font-manrope text-xs">⏱ {ex.frequencia}</p>}
                {ex.por_que && <p className="text-gray-400 print:text-black font-manrope text-xs italic">{ex.por_que}</p>}
              </div>
            ))}
          </section>
        )}

        <p className="text-gray-400 print:text-black text-center text-xs font-manrope pt-4 border-t border-gray-200 print:border-gray-300">
          Este material é de apoio ao autoconhecimento e não substitui acompanhamento psicológico ou médico profissional.
        </p>
      </div>
    </div>
  );
}
